/**
 * Утилиты доступа к глобальному `window.Telegram.WebApp` (Stage 5.X.2).
 *
 * SDK `@telegram-apps/sdk` тоже доступен, но эти обёртки минимальны и
 * не тянут весь SDK в auth-chunk (отлично для bundle-size). Полный SDK
 * подгрузим в Stage 5.X.3+ для расширенного функционала (MainButton,
 * BackButton, viewport, theme params).
 *
 * Защита от dev-режима в обычном браузере: если `window.Telegram.WebApp`
 * не определён, используем `VITE_DEV_INIT_DATA` env-fallback. На prod
 * env-переменная должна быть пустой — отсутствие initData → fail-fast.
 */

/**
 * Получить глобальный TelegramWebApp объект (если доступен).
 *
 * Returns: WebApp instance или `null` в dev-режиме / при отсутствии SDK.
 */
export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.Telegram?.WebApp ?? null;
}

/**
 * Извлечь Telegram WebApp initData (HMAC-подписанная query-string).
 *
 * Приоритет:
 * 1. `window.Telegram.WebApp.initData` (внутри Telegram).
 * 2. `import.meta.env.VITE_DEV_INIT_DATA` (dev fallback вне Telegram).
 * 3. `null` если ничего не доступно.
 *
 * Returns: непустая строка с initData или `null`.
 */
export function getInitData(): string | null {
  const webAppData = getWebApp()?.initData;
  if (webAppData && webAppData.length > 0) {
    return webAppData;
  }

  const devData = import.meta.env.VITE_DEV_INIT_DATA;
  if (devData && devData.length > 0) {
    return devData;
  }

  return null;
}

/**
 * Уведомить Telegram-клиент, что WebApp готов к показу (убирает loading
 * spinner Telegram'а).
 *
 * Идемпотентно — Telegram игнорирует повторные вызовы.
 */
export function notifyWebAppReady(): void {
  getWebApp()?.ready();
}

/**
 * Закрыть WebApp (вернуться в чат с ботом).
 *
 * Используется при logout (см. docs/tg-miniapp.md §8.1.3).
 */
export function closeWebApp(): void {
  getWebApp()?.close();
}

/**
 * Тип haptic feedback (см. docs/tg-miniapp.md §1.3 — animation principles).
 */
export type HapticImpact = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotification = 'error' | 'success' | 'warning';

/**
 * Тактильный отклик (поддерживается только на iOS/Android, не desktop).
 *
 * Безопасно — no-op если SDK отсутствует.
 */
export function hapticImpact(style: HapticImpact = 'light'): void {
  try {
    getWebApp()?.HapticFeedback.impactOccurred(style);
  } catch {
    // Silent — haptic — UX nice-to-have, не critical.
  }
}

export function hapticNotification(type: HapticNotification): void {
  try {
    getWebApp()?.HapticFeedback.notificationOccurred(type);
  } catch {
    // Silent — см. hapticImpact.
  }
}
